import { React, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import SalesList from './SalesList';

import LoadingAnimation from '../../../Animation/NewLoading.json';
import NormalSortButtonPath from '../../../image/GrayNormalSortButton.svg';
import NormalSortButtonDarkPath from '../../../image/GrayNormalSortButtonDark.svg';
import AscSortButtonPath from '../../../image/AscSortButton2.svg';
import AscSortButtonDarkPath from '../../../image/AscSortButtonDark2.svg';
import DescSortButtonPath from '../../../image/DescSortButton2.svg';
import DescSortButtonDarkPath from '../../../image/DescSortButtonDark2.svg';

export default function RevenueHistory(props) {
  const {
    selectedPartnerGym,
    isOpenPartnerGymSales,
    calcDateText,
    data,
    isSalesLoading,
    setIsSalesLoading
  } = props;

  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [division, setDivision] = useState('Total');
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [useData, setUseData] = useState([]);
  const [priceSortState, setPriceSortState] = useState('normal');
  const [itemSortState, setItemSortState] = useState('normal');
  const [dateSortState, setDateSortState] = useState('normal');

  // 데이터에서 결제 구분 뽑기 //
  useEffect(() => {
    const seenIds = new Set();
    const newSelectedPaymentOptions = [];

    for (const sale of data) {
      for (const option of sale.selectedPaymentOptions) {
        if (option.paymentOptionId === 0) continue;
        if (seenIds.has(option.paymentOptionId)) continue;

        seenIds.add(option.paymentOptionId);
        newSelectedPaymentOptions.push({
          paymentOptionId: option.paymentOptionId,
          paymentOptionName: option.paymentOptionName
        });
      }
    }

    setPaymentOptions(newSelectedPaymentOptions);
  }, [data]);

  // 데이터 필터링 + 정렬 //
  useEffect(() => {
    let filtered = [];

    if (division === 'Total') {
      filtered = data.slice();
    }
    else if (division === 'LinkPay') {
      for (const sale of data) {
        if (!sale.merchantUid) continue;

        const segments = sale.merchantUid.split('-');

        if (segments.length >= 4 && (segments[2] === '1' || segments[2] === '2')) {
          filtered.push(sale);
        }
      }
    }
    else if (division === 'Card') {
      for (const sale of data) {
        if (sale.cardPrice > 0) filtered.push(sale);
      }
    }
    else if (division === 'Cash') {
      for (const sale of data) {
        if (sale.cashPrice > 0) filtered.push(sale);
      }
    }
    else if (division === 'Not') {
      for (const sale of data) {
        if (sale.notReceivedPrice > 0) filtered.push(sale);
      }
    }
    else {
      for (const sale of data) {
        for (const option of sale.selectedPaymentOptions) {
          if (division === option.paymentOptionId) {
            filtered.push(sale);
          }
        }
      }
    }

    // 결제 내역 정렬 적용 (첫글자 기준, 한글 > 영문 > 숫자/특수문자 순) //
    if (itemSortState !== 'normal') {
      const getCharGroup = (ch) => {
        if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(ch)) return 0; // 한글
        if (/[a-zA-Z]/.test(ch)) return 1;            // 영문
        return 2;                                       // 숫자/특수문자
      };
      filtered.sort((a, b) => {
        const aFirst = (a.itemName || '').charAt(0);
        const bFirst = (b.itemName || '').charAt(0);
        const groupDiff = getCharGroup(aFirst) - getCharGroup(bFirst);
        if (groupDiff !== 0) return itemSortState === 'asc' ? groupDiff : -groupDiff;
        if (itemSortState === 'asc') return aFirst.localeCompare(bFirst, 'ko');
        return bFirst.localeCompare(aFirst, 'ko');
      });
    }

    // 결제금액 정렬 적용 //
    if (priceSortState !== 'normal') {
      filtered.sort((a, b) => {
        if (priceSortState === 'asc') return a.totalPrice - b.totalPrice;
        return b.totalPrice - a.totalPrice;
      });
    }

    // 결제일 정렬 적용 //
    if (dateSortState !== 'normal') {
      filtered.sort((a, b) => {
        if (dateSortState === 'asc') return a.approvalDate.localeCompare(b.approvalDate);
        return b.approvalDate.localeCompare(a.approvalDate);
      });
    }

    setUseData(filtered);

    setTimeout(() => setIsSalesLoading(false), 1000);
  }, [data, division, priceSortState, itemSortState, dateSortState]);

  // 정렬 상태 토글: normal → asc → desc → normal //
  function onClickPriceSort() {
    setItemSortState('normal');
    setDateSortState('normal');
    if (priceSortState === 'normal') setPriceSortState('desc');
    else if (priceSortState === 'desc') setPriceSortState('asc');
    else setPriceSortState('normal');
  }

  // 결제 내역 정렬 토글: normal → asc → desc → normal //
  function onClickItemSort() {
    setPriceSortState('normal');
    setDateSortState('normal');
    if (itemSortState === 'normal') setItemSortState('asc');
    else if (itemSortState === 'asc') setItemSortState('desc');
    else setItemSortState('normal');
  }

  // 결제일 정렬 토글: normal → asc → desc → normal //
  function onClickDateSort() {
    setPriceSortState('normal');
    setItemSortState('normal');
    if (dateSortState === 'normal') setDateSortState('asc');
    else if (dateSortState === 'asc') setDateSortState('desc');
    else setDateSortState('normal');
  }

  // 정렬 버튼 이미지 //
  function getSortButtonImg(state) {
    if (state === 'asc') return isDark ? AscSortButtonDarkPath : AscSortButtonPath;
    if (state === 'desc') return isDark ? DescSortButtonDarkPath : DescSortButtonPath;
    return isDark ? NormalSortButtonDarkPath : NormalSortButtonPath;
  }

  // 행 클릭시 //
  function onClickRow(rowData) {
    if (rowData.memberId === undefined) {
      alert(t('statistics.sales.alertNotRegistered'));
      return;
    }

    if (selectedPartnerGym === undefined || selectedPartnerGym.isMyStore) {
      window.open('/member/valid/detail/' + rowData.memberId, '_blank');
    }
    else {
      window.open('/member/partner_gym/detail/' + rowData.memberId, '_blank');
    }
  }

  // 날짜 포맷 //
  function formatShortDate(iso) {
    const d = iso.split('T')[0].split('-');
    const order = t('statistics.calendar.dateOrder');
    const sep = t('statistics.calendar.dateSeparator');

    if (order === 'MDY') return d[1] + sep + d[2] + sep + d[0].slice(2);
    return d[0].slice(2) + sep + d[1] + sep + d[2];
  }

  // 결제 수단 번역 //
  function translatePaymentOption(name) {
    const map = {
      '카드': t('statistics.sales.optionCard'),
      '현금': t('statistics.sales.optionCash'),
      '포인트': t('statistics.sales.optionPoint')
    };

    return map[name] || name;
  }

  // 결제 금액 표시 //
  function decideTotalPriceView(rowData) {
    if (rowData.selectedPaymentOptions === undefined || rowData.selectedPaymentOptions.length === 0) {
      if (rowData.cardPrice === 0 && rowData.cashPrice === 0) return t('statistics.sales.free');
      else if (rowData.cardPrice === 0) return rowData.cashPrice.toLocaleString('ko-KR') + t('statistics.sales.paymentCash');
      else if (rowData.cashPrice === 0) return rowData.cardPrice.toLocaleString('ko-KR') + t('statistics.sales.paymentCard');
      else return (parseInt(rowData.cashPrice) + parseInt(rowData.cardPrice)).toLocaleString('ko-KR') + t('statistics.sales.paymentMixed');
    }
    else {
      const allSame = rowData.selectedPaymentOptions.every(
        item => item.paymentOptionName === rowData.selectedPaymentOptions[0].paymentOptionName
      );
      const totalPrice = rowData.selectedPaymentOptions.reduce((sum, item) => sum + item.price, 0);

      if (allSame) return totalPrice.toLocaleString('ko-KR') + ' - ' + translatePaymentOption(rowData.selectedPaymentOptions[0].paymentOptionName);
      else return totalPrice.toLocaleString('ko-KR') + t('statistics.sales.paymentMixed');
    }
  }


  return (
    <TopLevelWrapper>
      <TitleWrapper>
        <TitleText>
          {calcDateText() + ' ' + t('statistics.sales.totalSalesHistory') + ' ( ' + useData.length + t('statistics.sales.caseSuffix') + ' )'}
        </TitleText>
        <DivisionWrapper>
          <DivisionText $isClicked={division === 'Total'} onClick={() => setDivision('Total')}>{t('statistics.sales.all')}</DivisionText>
          <DivisionText $isClicked={division === 'LinkPay'} onClick={() => setDivision('LinkPay')}>{t('statistics.sales.linkoPay')}</DivisionText>
          <DivisionText $isClicked={division === 'Card'} onClick={() => setDivision('Card')}>{t('statistics.sales.card')}</DivisionText>
          <DivisionText $isClicked={division === 'Cash'} onClick={() => setDivision('Cash')}>{t('statistics.sales.cash')}</DivisionText>
          {
            paymentOptions.map((option, optionIndex) => (
              <DivisionText
                key={'PaymentOption-' + optionIndex}
                $isClicked={division === option.paymentOptionId}
                onClick={() => setDivision(option.paymentOptionId)}>
                {option.paymentOptionName}
              </DivisionText>
            ))
          }
          <DivisionText $isClicked={division === 'Not'} onClick={() => setDivision('Not')}>{t('statistics.sales.notReceived')}</DivisionText>
        </DivisionWrapper>
      </TitleWrapper>

      {
        isSalesLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <TableWrapper>
              <TableHeader>
                <HeaderCell $width='140px'>{t('statistics.sales.colName')}</HeaderCell>
                <HeaderCellFlex>
                  {t('statistics.sales.colPaymentHistory')}
                  <SortButton onClick={onClickItemSort}>
                    <SortIcon src={getSortButtonImg(itemSortState)} />
                  </SortButton>
                </HeaderCellFlex>
                <HeaderCell $width='200px'>
                  {t('statistics.sales.colPaymentAmount')}
                  <SortButton onClick={onClickPriceSort}>
                    <SortIcon src={getSortButtonImg(priceSortState)} />
                  </SortButton>
                </HeaderCell>
                <HeaderCell $width='150px'>{t('statistics.sales.colNotReceived')}</HeaderCell>
                <HeaderCell $width='100px'>
                  {t('statistics.sales.colPaymentDate')}
                  <SortButton onClick={onClickDateSort}>
                    <SortIcon src={getSortButtonImg(dateSortState)} />
                  </SortButton>
                </HeaderCell>
              </TableHeader>

              <DivisionLine />

              <TableBody>
                {useData.length === 0 ?
                  <EmptyWrapper>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>
                      {t('statistics.sales.noSalesHistory')}
                    </Text>
                  </EmptyWrapper>
                  :
                  useData.map((rowData, index) => (
                    <TableRow
                      key={'SalesRow-' + index}
                      $isDark={isDark}
                      onClick={() => onClickRow(rowData)}>
                      <BodyCell $width='140px'>
                        {rowData.name === undefined ? t('statistics.sales.withdrawnMember') : rowData.name}
                      </BodyCell>
                      <BodyCellFlex>
                        {rowData.itemName}
                      </BodyCellFlex>
                      <BodyCell $width='200px'>
                        {decideTotalPriceView(rowData)}
                      </BodyCell>
                      <BodyCell $width='150px' $isRefund={rowData.isRefund}>
                        {rowData.notReceivedPrice > 0 ? rowData.notReceivedPrice.toLocaleString('ko-KR') : ''}
                      </BodyCell>
                      <BodyCell $width='100px'>
                        {formatShortDate(rowData.approvalDate)}
                      </BodyCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </TableWrapper>
            :
            <NotOpenTextWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenTextWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding-bottom: 10px;

  width: 100%;
`;

const TitleText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0;
  padding: 0;

  white-space: nowrap;
`;

const DivisionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  justify-content: flex-end;
  align-items: center;

  max-width: 440px;

  gap: 15px;
`;

const DivisionText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.$isClicked ? props.theme.brand500 : props.theme.subText};

  margin: 0;
  padding: 0;

  white-space: nowrap;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      color: ${props => props.theme.brand600};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 350px;

  background-color: ${props => props.theme.subBackground};
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 0 10px 0 12px;

  width: calc(100% - 22px);
  height: 30px;
`;

const HeaderCell = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: ${props => props.$width};

  gap: 5px;

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  white-space: nowrap;
`;

const HeaderCellFlex = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 0;

  gap: 5px;

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  white-space: nowrap;

  overflow: hidden;
`;

const DivisionLine = styled.div`
  width: calc(100% - 20px);
  height: 1px;

  margin: 0 10px;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  max-height: 350px;

  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.brand500};
    border-radius: 50px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 50px;
  }
`;

const TableRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 9px 9px 9px 11px;

  width: calc(100% - 22px);

  border-radius: 5px;
  border: 1px solid ${props => props.theme.subBackground};
  background-color: transparent;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    border: 1px solid ${props => props.theme.brand500Alpha50};
  }
`;

const BodyCell = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: ${props => props.$width};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.$isRefund ? props.theme.pointRed : props.theme.text};

  white-space: nowrap;

  overflow: hidden;
  text-overflow: ellipsis;

  transition: color 0.2s ease-in-out;
`;

const BodyCellFlex = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 0;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  white-space: nowrap;

  overflow: hidden;
  text-overflow: ellipsis;

  transition: color 0.2s ease-in-out;
`;

const EmptyWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100px;
`;

const SortButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  cursor: pointer;
`;

const SortIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const NotOpenTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;